import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import UserAvatar from '@/components/UserAvatar';
import AvatarSelectionGrid from '@/components/AvatarSelectionGrid';

interface ProfileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ProfileModal = ({ open, onOpenChange }: ProfileModalProps) => {
  const { displayName, avatarId, updateDisplayName } = useAuth();
  const { toast } = useToast();
  const [newName, setNewName] = useState(displayName || '');
  const [saving, setSaving] = useState(false);
  const [showAvatarGrid, setShowAvatarGrid] = useState(false);

  const nameUnchanged = newName.trim() === (displayName || '').trim();
  const nameEmpty = newName.trim() === '';

  const handleSaveName = async () => {
    if (nameUnchanged || nameEmpty) return;
    setSaving(true);
    try {
      await updateDisplayName(newName.trim());
      toast({ title: 'השם עודכן!' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setShowAvatarGrid(false);
    }
    onOpenChange(nextOpen);
  };

  const handleEditAvatar = () => {
    // Close the profile modal, then show fullscreen avatar picker
    onOpenChange(false);
    // Small delay so the dialog exit animation completes before the overlay mounts
    setTimeout(() => setShowAvatarGrid(true), 150);
  };

  const handleAvatarClose = () => {
    setShowAvatarGrid(false);
    // Re-open profile modal after avatar picker closes
    onOpenChange(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="bg-card border-border max-w-sm overflow-hidden" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">הגדרות פרופיל</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-6 pt-2">
            {/* Avatar section */}
            <div className="flex flex-col items-center gap-3">
              <UserAvatar avatarId={avatarId} displayName={displayName} size="lg" className="!h-24 !w-24" />
              <button
                onClick={handleEditAvatar}
                className="text-sm text-accent hover:text-accent/80 transition-colors"
              >
                שנה דמות
              </button>
            </div>

            {/* Display name section */}
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                שם תצוגה
              </label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="שם תצוגה"
                className="text-right bg-background border-border"
                dir="rtl"
              />
              <Button
                onClick={handleSaveName}
                disabled={nameUnchanged || nameEmpty || saving}
                className="w-full"
              >
                {saving ? 'שומר...' : 'שמור'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen avatar picker — rendered outside the dialog */}
      {showAvatarGrid && (
        <AvatarSelectionGrid onClose={handleAvatarClose} />
      )}
    </>
  );
};

export default ProfileModal;
